import React, { useState, useContext, useRef, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { SocketContext } from '../App';
import VideoGrid from '../components/VideoGrid';
import ChatPanel from '../components/ChatPanel';
import ControlBar from '../components/ControlBar';
import ParticipantsList from '../components/ParticipantsList';
import Whiteboard from '../components/Whiteboard';
import ReactionPanel from '../components/ReactionPanel';
import recordingManager from '../utils/RecordingManager';
import useWebRTC from '../hooks/useWebRTC';

const RoomContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #0f1419;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;

  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
  }
`;

const RoomInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const RoomTitle = styled.h2`
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const RoomId = styled.div`
  background: rgba(255, 107, 53, 0.2);
  color: #FF6B35;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 107, 53, 0.3);
    transform: scale(1.05);
  }
`;

const ParticipantCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const CountBadge = styled.div`
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.8rem;
`;

const StatusBadge = styled(motion.div)`
  background: ${props => props.color || 'rgba(255, 107, 53, 0.2)'};
  color: ${props => props.textColor || '#FF6B35'};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MainContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: ${props => props.chatopen === 'true' ? '1fr 350px' : '1fr'};
  grid-template-rows: 1fr auto;
  gap: 0;
  min-height: 0;
  position: relative;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const VideoSection = styled.div`
  position: relative;
  background: #1a1a2e;
  overflow: hidden;
  grid-row: 1 / 2;
`;

const ChatSection = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  grid-row: 1 / 3;
  z-index: 60;
  
  @media (max-width: 1024px) {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 350px;
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 500px) {
    width: 100%;
  }
`;

const ControlSection = styled.div`
  grid-row: 2 / 3;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 20, 25, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid rgba(255, 107, 53, 0.3);
  border-top: 3px solid #FF6B35;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingMessage = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(239, 68, 68, 0.1);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  text-align: center;
  padding: 2rem;
`;

const ErrorMessage = styled.div`
  color: #fca5a5;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const RetryButton = styled.button`
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  border: none;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  margin: 0.5rem;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  }
`;

const NotificationToast = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  border-left: 4px solid ${props => props.type === 'error' ? '#ef4444' : '#10b981'};
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-width: 300px;
`;

function MeetingRoom() {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket, connected } = useContext(SocketContext);
  
  const [chatOpen, setChatOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [whiteboardOpen, setWhiteboardOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const recordingTimerRef = useRef(null);

  React.useEffect(() => {
    if (!location.state && !localStorage.getItem('displayName')) {
      navigate(`/join/${roomId}`, { replace: true });
      return;
    }
  }, [location.state, roomId, navigate]);

  const userData = location.state || {
    displayName: localStorage.getItem('displayName') || 'Utente',
    videoEnabled: true,
    audioEnabled: true
  };

  const {
    localStream,
    remoteStreams,
    participants,
    messages,
    isConnected,
    videoEnabled,
    audioEnabled,
    isScreenSharing,
    error,
    reactions,
    handRaised,
    raisedHands,
    whiteboardData,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    sendMessage,
    sendReaction,
    toggleHand,
    sendWhiteboardData,
    clearWhiteboard,
    leaveRoom
  } = useWebRTC(roomId, userData, socket);

  useEffect(() => {
    if (recordingManager.getIsRecording()) {
      // Aggiorna i partecipanti nel recording manager quando cambiano
      const allParticipants = [
        {
          id: 'local',
          displayName: userData.displayName,
          isLocal: true,
          stream: localStream,
          audioMuted: !audioEnabled,
          videoMuted: !videoEnabled
        },
        ...participants.map(p => ({
          ...p,
          stream: remoteStreams[p.id],
          audioMuted: p.audioMuted || false,
          videoMuted: p.videoMuted || false
        }))
      ].filter(p => p.stream);
      
      recordingManager.updateParticipants(allParticipants);
    }
  }, [localStream, remoteStreams, participants, audioEnabled, videoEnabled, userData.displayName]);

  const isLoading = !connected || !localStream;
  const totalParticipants = participants.length + (localStream ? 1 : 0);
  const isHost = userData.role === 'host' || participants.length === 0;

  const handleLeaveRoom = () => {
    if (isRecording) {
      handleStopRecording();
    }
    leaveRoom();
    navigate('/', { replace: true });
  };

  const handleCopyRoomId = async () => {
    try {
      const joinUrl = `${window.location.origin}/join/${roomId}`;
      await navigator.clipboard.writeText(joinUrl);
      addNotification('success', 'Link sala copiato negli appunti!');
    } catch (err) {
      addNotification('error', 'Errore copia link');
    }
  };

  const addNotification = (type, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const handleToggleWhiteboard = () => {
    setWhiteboardOpen(!whiteboardOpen);
    if (!whiteboardOpen) {
      addNotification('success', 'Lavagna digitale aperta! Usa i controlli sotto per chiudere.');
    }
  };

  const handleSendReaction = (emoji) => {
    // Invia l'emoji selezionata come reazione
    if (socket) {
      socket.emit('send-reaction', {
        roomId: id,
        userId: socket.id,
        displayName: userData.displayName,
        emoji: emoji
      });
      
      // Mostra la reazione localmente
      addReaction({
        id: Date.now(),
        userId: socket.id,
        displayName: userData.displayName,
        emoji: emoji
      });
    }
  };

  const handleToggleHand = () => {
    toggleHand();
    addNotification('success', !handRaised ? 'Mano alzata!' : 'Mano abbassata');
  };

  const handleStartRecording = async () => {
    // Prepara tutti i partecipanti con i loro stream
    const allParticipants = [
      {
        id: 'local',
        displayName: userData.displayName,
        isLocal: true,
        stream: localStream,
        audioMuted: !audioEnabled,
        videoMuted: !videoEnabled
      },
      ...participants.map(p => ({
        ...p,
        stream: remoteStreams[p.id],
        audioMuted: p.audioMuted || false,
        videoMuted: p.videoMuted || false
      }))
    ].filter(p => p.stream); // Solo partecipanti con stream attivo

    const started = await recordingManager.startRecording(allParticipants, {
      width: 1920,
      height: 1080,
      videoBitsPerSecond: 2500000,
      audioBitsPerSecond: 128000
    });

    if (started) {
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      addNotification('success', '🔴 Registrazione avviata! Include tutti i partecipanti.');
    } else {
      addNotification('error', 'Impossibile avviare la registrazione');
    }
  };

  const handleStopRecording = () => {
    recordingManager.stopRecording();

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecording(false);
    setRecordingTime(0);
    addNotification('success', '⏹️ Registrazione salvata in Downloads!');
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  };

  useEffect(() => {
    return () => {
      if (isRecording) {
        handleStopRecording();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <RoomContainer>
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingMessage>
            {!connected ? 'Connessione al server...' : 'Accesso a camera e microfono...'}
          </LoadingMessage>
        </LoadingOverlay>
      </RoomContainer>
    );
  }

  if (error) {
    return (
      <RoomContainer>
        <ErrorOverlay>
          <ErrorMessage>❌ Errore</ErrorMessage>
          <div>{error}</div>
          <div>
            <RetryButton onClick={() => window.location.reload()}>
              🔄 Riprova
            </RetryButton>
            <RetryButton onClick={() => navigate('/')}>
              🏠 Torna Home
            </RetryButton>
          </div>
        </ErrorOverlay>
      </RoomContainer>
    );
  }

  return (
    <RoomContainer>
      <AnimatePresence>
        {notifications.map((n) => (
          <NotificationToast
            key={n.id}
            type={n.type}
            initial={{ opacity: 0, x: 100, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.9 }}
          >
            {n.message}
          </NotificationToast>
        ))}
      </AnimatePresence>

      <TopBar>
        <RoomInfo>
          <RoomTitle>💡 Sala LuminAI</RoomTitle>
          <RoomId onClick={handleCopyRoomId} title="Clicca per copiare link invito">
            ID: {roomId}
          </RoomId>
          
          {isScreenSharing && (
            <StatusBadge
              color="rgba(255, 107, 53, 0.2)"
              textColor="#FF6B35"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              <span>🖥️</span>
              <span>Condivisione</span>
            </StatusBadge>
          )}

          {isRecording && (
            <StatusBadge
              color="rgba(239, 68, 68, 0.2)"
              textColor="#ef4444"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              <motion.span
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                ⏺️
              </motion.span>
              <span>Registrazione</span>
            </StatusBadge>
          )}

          {raisedHands.length > 0 && (
            <StatusBadge
              color="rgba(245, 158, 11, 0.2)"
              textColor="#f59e0b"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              <span>✋</span>
              <span>{raisedHands.length} mano/i alzata/e</span>
            </StatusBadge>
          )}
        </RoomInfo>
        
        <ParticipantCount>
          <span>👥</span>
          <CountBadge>{totalParticipants}</CountBadge>
          <span>partecipant{totalParticipants !== 1 ? 'i' : 'e'}</span>
        </ParticipantCount>
      </TopBar>

      <MainContent chatopen={chatOpen ? 'true' : 'false'}>
        <VideoSection>
          <VideoGrid
            localStream={localStream}
            remoteStreams={remoteStreams}
            participants={participants}
          />
        </VideoSection>

        <AnimatePresence>
          {chatOpen && (
            <ChatSection
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              <ChatPanel
                messages={messages}
                onSendMessage={sendMessage}
                onClose={() => setChatOpen(false)}
                participants={[...participants, { 
                  id: 'local', 
                  displayName: userData.displayName 
                }]}
              />
            </ChatSection>
          )}
        </AnimatePresence>

        <ControlSection>
          <ControlBar
            videoEnabled={videoEnabled}
            audioEnabled={audioEnabled}
            screenSharing={isScreenSharing}
            chatOpen={chatOpen}
            participantsOpen={participantsOpen}
            whiteboardOpen={whiteboardOpen}
            handRaised={handRaised}
            isRecording={isRecording}
            isHost={isHost}
            onToggleVideo={toggleVideo}
            onToggleAudio={toggleAudio}
            onToggleScreenShare={toggleScreenShare}
            onToggleChat={() => setChatOpen(!chatOpen)}
            onToggleParticipants={() => setParticipantsOpen(!participantsOpen)}
            onToggleWhiteboard={handleToggleWhiteboard}
            onSendReaction={handleSendReaction}
            onToggleHand={handleToggleHand}
            onToggleRecording={handleToggleRecording}
            onLeaveRoom={handleLeaveRoom}
            participantCount={totalParticipants}
            unreadMessages={0}
            connectionQuality={connected && isConnected ? 'excellent' : 'poor'}
          />
        </ControlSection>
      </MainContent>

      <ReactionPanel reactions={reactions} />

      <AnimatePresence>
        {participantsOpen && (
          <ParticipantsList
            participants={[
              { 
                id: 'local', 
                displayName: userData.displayName,
                role: isHost ? 'host' : 'participant',
                connected: true,
                videoEnabled,
                audioEnabled,
                handRaised
              },
              ...participants.map(p => ({
                ...p,
                handRaised: raisedHands.includes(p.id)
              }))
            ]}
            onClose={() => setParticipantsOpen(false)}
            currentUserId="local"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {whiteboardOpen && (
          <Whiteboard
            onClose={() => setWhiteboardOpen(false)}
            onDraw={sendWhiteboardData}
            initialData={whiteboardData}
          />
        )}
      </AnimatePresence>
    </RoomContainer>
  );
}

export default MeetingRoom;
